import { __rest } from "tslib";
import { groupBy, isNil } from 'ramda';
import uniqueId from 'lodash/uniqueId';
import { RuleFilterValueType, RuleType, Status } from '@/search-for-retail/models';
import { SearchForRetail } from '@/constants';
import { DEFAULT_RELEVANCY } from '@/search-for-retail/biasing-strategy/biasing-strategy.constants';
import { getSortedByPosition } from '@/search-for-retail/rule/form/ranking/pinned-products/pinned-products.utils';
const parseSearchFilters = (searchFilters) => searchFilters.map(({ value }) => ({
    filterType: SearchForRetail.FilterType.SEARCH,
    value,
}));
const parseRangeFilters = (rangeFilters) => rangeFilters.map(({ range: { high, low, exclude }, field }) => ({
    filterType: SearchForRetail.FilterType.RANGE,
    high,
    low,
    exclude,
    field,
}));
const parseValueFilters = (valueFilters) => valueFilters.map((_a) => {
    var { exclude, type } = _a, filter = __rest(_a, ["exclude", "type"]);
    return (Object.assign({ filterType: exclude ? SearchForRetail.FilterType.EXCLUSION : SearchForRetail.FilterType.FILTERED_BY, valueType: type }, filter));
});
const parseAttributesRangeFilters = (filters) => filters.map(({ range: { high, low, exclude }, field }) => ({
    filterType: SearchForRetail.FilterType.RANGE,
    field,
    type: RuleFilterValueType.Numeric,
    high,
    low,
    exclude,
}));
const parseAttributeValueFilters = (filters) => filters.reduce((acc, _a) => {
    var { value, numberValue } = _a, filter = __rest(_a, ["value", "numberValue"]);
    const existIndex = acc.findIndex(({ type, field, exclude }) => type === filter.type && field === filter.field && exclude === filter.exclude);
    if (existIndex !== -1) {
        if (filter.type === RuleFilterValueType.Textual) {
            acc[existIndex].values.push(value);
        }
        else {
            acc[existIndex].numberValues.push(numberValue);
        }
    }
    else {
        acc.push(Object.assign(Object.assign(Object.assign({}, filter), { type: filter.type, filterType: SearchForRetail.FilterType.FILTERED_BY }), (filter.type === RuleFilterValueType.Textual ? {
            values: [value],
        } : {
            numberValues: [numberValue],
        })));
    }
    return acc;
}, []);
const parseQueryPatternTriggers = (triggers) => triggers.map(({ type, value }) => ({
    _id: uniqueId('queryPattern-'),
    triggerType: type,
    value,
}));
const parseCustomParameterTriggers = (triggers) => triggers.map(({ value, key }) => ({
    _id: uniqueId('customParameter-'),
    triggerType: SearchForRetail.TriggerType.CUSTOM_PARAMETER,
    value,
    key,
}));
const parseSelectedRefinementTriggers = (triggers) => triggers.map(({ type, field, value, range, }) => {
    if (isNil(type) && isNil(value)) {
        return {
            _id: uniqueId('selectedRefinement-'),
            field,
            triggerType: SearchForRetail.TriggerType.NAVIGATION_SELECTED,
        };
    }
    return Object.assign(Object.assign({ _id: uniqueId('selectedRefinement'), triggerType: type, field }, (!isNil(value) && { value })), (range && { low: range.low, high: range.high }));
});
const parseTriggers = ({ queryPatternTriggers = [], customParameterTriggers = [], selectedRefinementTriggers = [], }) => [
    ...parseQueryPatternTriggers(queryPatternTriggers),
    ...parseCustomParameterTriggers(customParameterTriggers),
    ...parseSelectedRefinementTriggers(selectedRefinementTriggers),
];
const getResultManagement = (searchFilters = [], rangeFilters = [], valueFilters = []) => [
    ...parseSearchFilters(searchFilters),
    ...parseRangeFilters(rangeFilters),
    ...parseValueFilters(valueFilters),
];
const getAttributeFilters = (attributeFilters) => (attributeFilters.reduce((acc, group) => {
    const filters = [
        ...parseAttributesRangeFilters((group === null || group === void 0 ? void 0 : group.rangeFilters) || []),
        ...parseAttributeValueFilters((group === null || group === void 0 ? void 0 : group.valueFilters) || []),
    ];
    if (filters.length) {
        acc.push(filters);
    }
    return acc;
}, []));
export const computeDateBasedStatus = ({ activeFrom, activeTo }) => {
    const currentDateTime = Date.now();
    if (activeFrom && currentDateTime < activeFrom)
        return Status.Scheduled;
    if (activeTo && activeTo < currentDateTime)
        return Status.Expired;
    return Status.Published;
};
/**
 * A rule's presentational state: its Status, or the A/B-test state for an
 * experiment. Not a Status value — an experiment's status is still whatever
 * its schedule says; this is only how the rule is *presented* and filtered.
 */
export const EXPERIMENT_DISPLAY_STATE = 'experiment';
const LIFECYCLE_STATUSES = new Set([Status.Draft, Status.Archived]);
/**
 * The single precedence behind both the campaign calendar's legend colours and
 * the rules table's Status filter, so the two can't disagree about what a rule
 * "is". Lifecycle statuses (Draft/Archived) win outright; then Expired beats an
 * A/B test, so an expired experiment reads as expired — the deliberate
 * S4R-11315 legend decision.
 */
export const getRuleDisplayState = ({ status, type }) => {
    if (LIFECYCLE_STATUSES.has(status)) {
        return status;
    }
    if (status === Status.Expired) {
        return Status.Expired;
    }
    if (type === RuleType.Experiment) {
        return EXPERIMENT_DISPLAY_STATE;
    }
    return status;
};
// BE-stored lifecycle statuses that take precedence over date-based computation.
const WIRE_STATUS_TO_STATUS = {
    DRAFT: Status.Draft,
    ARCHIVED: Status.Archived,
};
export const parseRuleVariant = (_a) => {
    var _b, _c, _d, _e;
    var { searchFilters, valueFilters, rangeFilters, biasingProfileName, boostedProductBuckets, buriedProductBuckets, productIdFilter, attributeFilters, template, pinnedProducts, removedGlobalFacets, hiddenDynamicFacets, ruleFacets, includeDynamicFacets, hiddenRefinements, pinnedRefinements } = _a, model = __rest(_a, ["searchFilters", "valueFilters", "rangeFilters", "biasingProfileName", "boostedProductBuckets", "buriedProductBuckets", "productIdFilter", "attributeFilters", "template", "pinnedProducts", "removedGlobalFacets", "hiddenDynamicFacets", "ruleFacets", "includeDynamicFacets", "hiddenRefinements", "pinnedRefinements"]);
    return (Object.assign(Object.assign({}, model), { template, resultManagementRefinements: getResultManagement(searchFilters, rangeFilters, valueFilters), searchFilters: (searchFilters || []).map(({ value }) => value), productIdFilter: productIdFilter ? Object.assign(Object.assign({}, productIdFilter), { includedProductIds: (_b = productIdFilter.includedProductIds) !== null && _b !== void 0 ? _b : [], excludedProductIds: (_c = productIdFilter.excludedProductIds) !== null && _c !== void 0 ? _c : [], includeOutOfStock: (_d = productIdFilter.includeOutOfStock) !== null && _d !== void 0 ? _d : true }) : productIdFilter, pinnedProducts: getSortedByPosition((_e = pinnedProducts) !== null && _e !== void 0 ? _e : []).map((pinnedProduct) => {
            var _a;
            return (Object.assign(Object.assign({}, pinnedProduct), { includeOutOfStock: (_a = pinnedProduct === null || pinnedProduct === void 0 ? void 0 : pinnedProduct.includeOutOfStock) !== null && _a !== void 0 ? _a : true }));
        }), attributeFilters: getAttributeFilters(attributeFilters || []), biasingStrategy: biasingProfileName !== null && biasingProfileName !== void 0 ? biasingProfileName : DEFAULT_RELEVANCY, boostedProductBuckets: boostedProductBuckets === null || boostedProductBuckets === void 0 ? void 0 : boostedProductBuckets.map(({ products }) => ({ products: products !== null && products !== void 0 ? products : [] })), buriedProductBuckets: buriedProductBuckets === null || buriedProductBuckets === void 0 ? void 0 : buriedProductBuckets.map(({ products }) => ({ products: products !== null && products !== void 0 ? products : [] })), removedGlobalFacets: removedGlobalFacets || [], hiddenDynamicFacets: hiddenDynamicFacets || [], ruleFacets: ruleFacets || [], includeDynamicFacets: includeDynamicFacets !== null && includeDynamicFacets !== void 0 ? includeDynamicFacets : true, hiddenRefinements: hiddenRefinements || [], pinnedRefinements: pinnedRefinements || [] }));
};
export const parseVariant = (_a) => {
    var { ruleVariant } = _a, model = __rest(_a, ["ruleVariant"]);
    return (Object.assign(Object.assign({}, model), parseRuleVariant(ruleVariant)));
};
export const parseRule = (_a) => {
    var _b, _c, _d, _e;
    var { searchFilters, valueFilters, rangeFilters, boostedProductBuckets, buriedProductBuckets, biasingProfileName, includedNavigations, pinnedProducts, pinnedRefinements, productIdFilter, attributeFilters, productVisibilityBias, template, triggerSets = [], activeFrom, activeTo, variants: experimentVariants = [], type, removedGlobalFacets, hiddenDynamicFacets, ruleFacets, includeDynamicFacets, hiddenRefinements } = _a, model = __rest(_a, ["searchFilters", "valueFilters", "rangeFilters", "boostedProductBuckets", "buriedProductBuckets", "biasingProfileName", "includedNavigations", "pinnedProducts", "pinnedRefinements", "productIdFilter", "attributeFilters", "productVisibilityBias", "template", "triggerSets", "activeFrom", "activeTo", "variants", "type", "removedGlobalFacets", "hiddenDynamicFacets", "ruleFacets", "includeDynamicFacets", "hiddenRefinements"]);
    let variants = experimentVariants;
    if (type === RuleType.Regular) {
        const mainRuleVariant = {
            active: true,
            ruleVariant: {
                searchFilters,
                valueFilters,
                rangeFilters,
                biasingProfileName,
                boostedProductBuckets,
                buriedProductBuckets,
                includedNavigations,
                pinnedRefinements,
                productVisibilityBias,
                productIdFilter: Object.assign(Object.assign({}, productIdFilter), { includedProductIds: (_b = productIdFilter === null || productIdFilter === void 0 ? void 0 : productIdFilter.includedProductIds) !== null && _b !== void 0 ? _b : [], excludedProductIds: (_c = productIdFilter === null || productIdFilter === void 0 ? void 0 : productIdFilter.excludedProductIds) !== null && _c !== void 0 ? _c : [] }),
                attributeFilters,
                pinnedProducts: getSortedByPosition((_d = pinnedProducts) !== null && _d !== void 0 ? _d : []).map((pinnedProduct) => {
                    var _a;
                    return (Object.assign(Object.assign({}, pinnedProduct), { includeOutOfStock: (_a = pinnedProduct === null || pinnedProduct === void 0 ? void 0 : pinnedProduct.includeOutOfStock) !== null && _a !== void 0 ? _a : true }));
                }),
                removedGlobalFacets: removedGlobalFacets || [],
                hiddenDynamicFacets: hiddenDynamicFacets || [],
                ruleFacets: ruleFacets || [],
                includeDynamicFacets,
                hiddenRefinements: hiddenRefinements || [],
                template,
            },
        };
        variants = [mainRuleVariant];
    }
    const ruleVariants = variants.map(parseVariant);
    const { activeVariants, inactiveVariants = [] } = groupBy(({ active }) => (active ? 'activeVariants' : 'inactiveVariants'), ruleVariants);
    const lifecycleStatus = model.status ? WIRE_STATUS_TO_STATUS[model.status] : undefined;
    let status;
    if (lifecycleStatus) {
        status = lifecycleStatus;
    }
    else if (model.status === 'PUBLISHED' || model.status == null) {
        status = computeDateBasedStatus({ activeFrom, activeTo });
    }
    else {
        console.warn(`[parseRule] Unrecognised status value "${model.status}" — falling back to date-based computation.`);
        status = computeDateBasedStatus({ activeFrom, activeTo });
    }
    return Object.assign(Object.assign({}, model), { 
        // BE omits null fields, so an Archived rule arrives without `priority` at all.
        priority: (_e = model.priority) !== null && _e !== void 0 ? _e : null, triggerSets: triggerSets.map(parseTriggers), dateRange: {
            activeFrom,
            activeTo,
        }, status,
        type, ruleVariants: activeVariants, inactiveRuleVariants: inactiveVariants });
};
